import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, LoadingGrid, PageHeader, StatusBadge } from "@/components/bits";
import { PostPaymentDialog } from "@/components/PostPaymentDialog";
import api, { errMsg, fmtDate, inr } from "@/lib/api";
import { payWithPayU } from "@/lib/payu";

export default function EmployerJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/employer/jobs").then(({ data }) => setJobs(data.jobs)).catch(() => setJobs([]));
  useEffect(() => {
    load();
  }, []);

  const submit = async (job, mode = "") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/employer/jobs/${job.id}/submit`, { posting_payment: mode });
      if (data.needs_payment) {
        toast.success("Complete payment to submit for review");
        const { data: pay } = await api.post("/payments/checkout", {
          kind: "job_post", ref_id: job.id, origin_url: window.location.origin,
        });
        payWithPayU(pay.payu_url, pay.params);
        return;
      }
      toast.success("Job submitted for admin review");
      setPayFor(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const close = async (job) => {
    try {
      await api.post(`/employer/jobs/${job.id}/close`);
      toast.success("Job closed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!jobs) return <LoadingGrid count={3} />;

  return (
    <div data-testid="employer-jobs-page">
      <PageHeader title="Jobs" description="Create, submit and track all your job postings.">
        <Button onClick={() => navigate("/employer/jobs/new")} className="rounded-full bg-[#0B1B3D]" data-testid="post-job-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Post a Job
        </Button>
      </PageHeader>

      {jobs.length === 0 ? (
        <EmptyState
          title="No active jobs yet."
          description="Post your first job and it will go through a quick admin quality review before going live."
          cta="Post Your First Job"
          onCta={() => navigate("/employer/jobs/new")}
          testId="jobs-empty"
        />
      ) : (
        <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="jobs-table">
            <thead className="bg-[#F8FAFC] border-b border-border/60">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-5 py-3.5 font-semibold">Job</th>
                <th className="px-5 py-3.5 font-semibold">Hiring via</th>
                <th className="px-5 py-3.5 font-semibold">Status</th>
                <th className="px-5 py-3.5 font-semibold">Applications</th>
                <th className="px-5 py-3.5 font-semibold">Created</th>
                <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-border/40 hover:bg-[#F8FAFC] transition-colors" data-testid={`job-row-${j.id}`}>
                  <td className="px-5 py-4">
                    <Link to={`/employer/jobs/${j.id}`} className="font-semibold text-[#0B1B3D] hover:text-[#2563EB]">
                      {j.title}
                    </Link>
                    <p className="text-xs text-muted-foreground mt-0.5">{j.location} · {inr(j.salary_min)}–{inr(j.salary_max)}</p>
                    {j.status === "correction_required" && j.review_history?.length > 0 && (
                      <p className="text-xs text-orange-700 mt-1.5 bg-orange-50 rounded px-2 py-1 inline-block">
                        Admin: {j.review_history[j.review_history.length - 1].reason}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs capitalize">{(j.hiring_preference || "own").replace(/_/g, " ")}</td>
                  <td className="px-5 py-4">
                    <StatusBadge status={j.status} />
                    {j.status === "draft" && j.payment_pending && (
                      <p className="text-[11px] text-amber-700 font-semibold mt-1" data-testid={`payment-pending-${j.id}`}>Payment pending</p>
                    )}
                  </td>
                  <td className="px-5 py-4 font-semibold">{j.application_count}</td>
                  <td className="px-5 py-4 text-xs text-muted-foreground">{fmtDate(j.created_at)}</td>
                  <td className="px-5 py-4 text-right space-x-2">
                    <Link to={`/employer/jobs/${j.id}`}>
                      <Button size="sm" variant="outline" className="rounded-full" data-testid={`view-job-${j.id}`}>Pipeline</Button>
                    </Link>
                    {j.status === "draft" && (
                      <Button size="sm" className="rounded-full bg-[#2563EB]" onClick={() => setPayFor(j)} data-testid={`pay-publish-${j.id}`}>
                        Pay & Publish
                      </Button>
                    )}
                    {j.status === "correction_required" && (
                      <Button size="sm" className="rounded-full bg-[#2563EB]" onClick={() => submit(j)} data-testid={`submit-job-${j.id}`}>
                        Resubmit
                      </Button>
                    )}
                    {j.status === "published" && (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => close(j)} data-testid={`close-job-${j.id}`}>
                        Close
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PostPaymentDialog open={!!payFor} onClose={() => setPayFor(null)} onChoose={(mode) => submit(payFor, mode)} busy={busy} />
    </div>
  );
}
